import { useEffect, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { geometryApi } from "../../api/blades";
import CombinedMECO from "./CombinedMECO";
import Assembly3DView from "./Assembly3DView";
import { useChartColors } from "../../chartTheme";

const DEG2RAD = Math.PI / 180;

const MODES = [
  { key: "hs",   label: "H-S"  },
  { key: "wrap", label: "Wrap" },
  { key: "3d",   label: "3D"   },
];

const axisBase = {
  gridcolor: "#e5e7eb", linecolor: "#d1d5db", tickcolor: "#9ca3af",
  tickfont: { size: 10 }, zeroline: false,
  showspikes: true, spikemode: "across", spikecolor: "#9ca3af",
  spikethickness: 1, spikedash: "dot",
};

const commonLayout = {
  paper_bgcolor: "transparent", plot_bgcolor: "#f9fafb",
  showlegend: true,
  legend: {
    font: { size: 9 }, bgcolor: "rgba(255,255,255,0.88)",
    bordercolor: "#e5e7eb", borderwidth: 1,
    x: 0, xanchor: "left", y: 1, yanchor: "top",
  },
  hovermode: "closest", dragmode: "pan",
};

const commonConfig = {
  displayModeBar: "hover",
  modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
  scrollZoom: true, responsive: true, displaylogo: false,
};

export default function AssemblyPreviewPanel({ members = [] }) {
  const { WRAP_PALETTE } = useChartColors();
  const [mode, setMode]         = useState("hs");
  const [viewsMap, setViewsMap] = useState({});
  const containerRef            = useRef(null);

  // Fire synthetic resize events when the panel is resized by dragging the divider.
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => window.dispatchEvent(new Event("resize")));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Fetch blade views for each unique design ID.
  useEffect(() => {
    const ids = [...new Set(members.map(m => m.blade_design?.id).filter(Boolean))];
    ids.forEach(id => {
      if (viewsMap[id]) return;
      geometryApi.bladeViews(id)
        .then(({ data }) => setViewsMap(prev => ({ ...prev, [id]: data })))
        .catch(() => {});
    });
  }, [members]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build wrap traces: top-down (r·cos θ, r·sin θ) view per member.
  const wrapTraces = members.flatMap(member => {
    const views = viewsMap[member.blade_design?.id];
    if (!views?.wrap?.length) return [];
    const color = WRAP_PALETTE[member.position % WRAP_PALETTE.length];
    const dname = member.blade_design?.name ?? "";
    const mx = !!member.mirror_x;  // mirror about y-z plane: x → -x
    const sx = mx ? -1 : 1;
    return views.wrap.map(l => ({
      type: "scatter", mode: "lines",
      name: `${dname} · ${l.name}`,
      x: l.theta_deg.map((t, j) => sx * l.r[j] * Math.cos(t * DEG2RAD)),
      y: l.theta_deg.map((t, j) => l.r[j] * Math.sin(t * DEG2RAD)),
      line: { color, width: 1.5, shape: "spline" },
    }));
  });

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Preview
        </span>
        <div className="flex rounded border border-gray-200 overflow-hidden ml-auto">
          {MODES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              className={`px-2.5 py-0.5 text-xs font-medium transition-colors ${
                mode === key
                  ? "bg-bh-600 text-white"
                  : "bg-white text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      <div className="flex-1 min-h-0">
        {mode === "hs" && <CombinedMECO members={members} height="100%" />}

        {mode === "wrap" && (
          wrapTraces.length === 0 ? (
            <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-6">
              No wrap data — complete MECO and Beta Angles tabs for each design first.
            </div>
          ) : (
            <Plot
              data={wrapTraces}
              layout={{
                ...commonLayout,
                margin: { l: 50, r: 14, t: 10, b: 40 },
                xaxis: { ...axisBase, title: { text: "x [mm]", font: { size: 11 } } },
                yaxis: {
                  ...axisBase,
                  title: { text: "y [mm]", font: { size: 11 } },
                  scaleanchor: "x", scaleratio: 1,
                },
              }}
              config={commonConfig}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
            />
          )
        )}

        {mode === "3d" && <Assembly3DView members={members} height="100%" />}
      </div>
    </div>
  );
}
